import { useState } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Megaphone } from "lucide-react";
import { PageHeader } from "@/components/layout/page-header";
import { Segmented } from "@/components/layout/segmented";
import { ProdutorShell } from "@/components/mercado/produtor-shell";
import { QuadroPracaConectado } from "@/components/mercado/quadro-praca";
import { Comparacao } from "@/components/mercado/delta";
import { CardSemanaASemana } from "@/components/mercado/card-semana-a-semana";
import { ChipReferencial } from "@/components/mercado/status-chip";
import { useAuth } from "@/lib/auth-context";
import {
  useDefinirPraca,
  useHistoricoCotacoes,
  useMinhasPorSemana,
  useMinhasVsPraca,
  usePracaDaOrg,
  usePracas,
} from "@/hooks/use-cotacoes";
import { fmtComparacao, fmtKgBR, fmtMoedaBR, fmtDataCurtaBR } from "@/lib/formato-mercado";
import { OFERTAS_ATIVAS } from "@shared/mercado-ofertas";
import { PRODUTOS, chaveProduto, type ChaveProduto } from "@shared/mercado-produtos";
import { labelReferencial } from "@shared/schema";
import type { MediaPorReferencialDTO, MinhasVsPracaDTO, PeriodoHistorico } from "@shared/mercado-cotacoes";

/**
 * Cotações — "quanto a praça paga, e como as minhas vendas ficaram em relação a
 * ela".
 *
 * **Sem gráfico, em lugar nenhum.** Preço se lê em lista: quadro em cima, semana
 * a semana embaixo. Decisão fechada com o Hugo (07–08/09) — sparkline, linha e
 * eixo não voltam.
 *
 * ⚠️ Nada nesta tela calcula dinheiro. Média ponderada, delta, mínimo, máximo e
 * variação do período chegam prontos do servidor; aqui só se formata.
 */

const PERIODOS: Array<{ id: PeriodoHistorico; label: string }> = [
  { id: "8s", label: "8 semanas" },
  { id: "3m", label: "3 meses" },
  { id: "safra", label: "Safra" },
];

export default function MercadoCotacoes() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const ativo = user?.orgTipo !== "COMPRADOR";
  const [periodo, setPeriodo] = useState<PeriodoHistorico>("8s");
  const [produto, setProduto] = useState<ChaveProduto>(PRODUTOS[0]);

  const { data: pracas = [] } = usePracas();
  const { data: vsPraca } = useMinhasVsPraca("mes", ativo);
  const { data: minhasPorSemana } = useMinhasPorSemana(produto, periodo, ativo);
  // A praça vem de `minhas-vs-praca`, com queda para a padrão — ver `usePracaDaOrg`.
  const { praca } = usePracaDaOrg();
  const definirPraca = useDefinirPraca();

  return (
    <ProdutorShell>
      <PageHeader
        titulo="Cotações"
        descricao="Quanto a praça está pagando, e como as suas vendas ficaram em relação a ela."
        empilharQuandoEstreito
        acoesSecundarias={
          <Segmented
            opcoes={PERIODOS}
            valor={periodo}
            onChange={setPeriodo}
            prefixoTestId="chip-periodo"
          />
        }
        acao={OFERTAS_ATIVAS ? (
          <Button onClick={() => setLocation("/mercado/ofertas/nova")} data-testid="button-ofertar-carga">
            <Megaphone className="h-4 w-4 mr-1.5" />Ofertar carga
          </Button>
        ) : undefined}
      />

      {/* Um filtro só: a praça. Trocar aqui GRAVA a escolha na organização
          (`PATCH praca`) — o produtor olha para uma praça, não fica reescolhendo
          a cada visita. */}
      {pracas.length > 1 && (
        <div className="flex items-center gap-2">
          <span className="text-xs uppercase text-muted-foreground">Praça</span>
          <Select
            value={praca ?? ""}
            onValueChange={v => definirPraca.mutate(v)}
            disabled={definirPraca.isPending}
          >
            <SelectTrigger className="w-56" data-testid="select-praca"><SelectValue placeholder="Escolha a praça" /></SelectTrigger>
            <SelectContent>
              {pracas.map(p => (
                <SelectItem key={p.slug} value={p.slug}>{p.nome} · {p.uf}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Duas colunas INDEPENDENTES, como na home: nunca grade 2×2, senão a
          coluna curta herda a altura da longa e sobra vão. */}
      <div className="grid gap-4 md:grid-cols-[1.35fr_1fr] md:items-start">
        <div className="min-w-0">
          <QuadroPracaConectado />
        </div>
        <div className="min-w-0">
          <CardMinhasVsPraca dados={vsPraca} />
        </div>
      </div>

      {/* `minhas` é o que dá as colunas "você" — o comprador usa o mesmo card
          sem elas. A consulta continua aqui, na tela que tem vendas. */}
      <CardSemanaASemana
        praca={praca}
        produto={produto}
        onProduto={setProduto}
        periodo={periodo}
        minhas={minhasPorSemana}
      />
    </ProdutorShell>
  );
}

/** "Suas vendas × praça · mês": barra por produto com a marca da praça. */
function CardMinhasVsPraca({ dados }: { dados: MinhasVsPracaDTO | undefined }) {
  if (!dados) {
    return <div className="rounded-lg border bg-card h-56 animate-pulse" data-testid="card-minhas-vs-praca-carregando" />;
  }
  const comVenda = dados.produtos.filter(p => p.meuPreco != null);
  // A escala é o MAIOR entre os dois lados: com escala só nos meus preços, uma
  // praça acima de todos eles estouraria a barra para fora do card.
  const teto = dados.produtos.reduce((m, p) => Math.max(m, p.meuPreco ?? 0, p.precoPraca ?? 0), 0);

  return (
    <section className="rounded-lg border bg-card shadow-xs" data-testid="card-minhas-vs-praca">
      <header className="border-b px-3 py-2">
        <p className="text-sm font-semibold">Suas vendas × praça · mês</p>
      </header>

      {comVenda.length === 0 ? (
        <div className="px-3 py-8 text-center">
          <p className="text-sm font-medium">Nenhuma venda emitida no mês</p>
          <p className="text-xs text-muted-foreground mt-1">Quando você emitir um romaneio, o preço médio dele aparece aqui ao lado do da praça.</p>
        </div>
      ) : (
        <ul className="p-3 space-y-3">
          {comVenda.map(p => (
            <li key={`${p.variedade}-${p.qualidade ?? ""}`} className="space-y-1" data-testid={`vs-praca-${p.variedade}-${p.qualidade ?? "s"}`}>
              {/* Mesmo layout de duas linhas do quadro, e pelo mesmo motivo: a
                  comparação no modo VENDA não cabe ao lado do preço. */}
              <div className="flex items-start justify-between gap-3 text-xs">
                <span className="font-medium truncate">{p.label}</span>
                <span className="font-mono tabular-nums font-semibold shrink-0">{fmtMoedaBR(p.meuPreco!)}</span>
              </div>
              <div className="flex items-start justify-between gap-3">
                <Comparacao valor={p.delta} testId={`comparacao-${p.variedade}-${p.qualidade ?? "s"}`} />
                {/* A etiqueta por linha, sob o preço — a mesma do quadro. No
                    cabeçalho do card ela ficava longe demais dos números que
                    qualifica, e é a leitura errada DELES que ela existe para
                    evitar. */}
                <span className="shrink-0 text-[11.5px] text-muted-foreground whitespace-nowrap">
                  {labelReferencial(dados.referencial)}
                </span>
              </div>
              {/* A marca vertical é a praça; a barra preenchida é o SEU preço.
                  Duas barras lado a lado dobrariam a altura do card sem dizer
                  nada que a marca já não diga. */}
              <div className="relative h-2 rounded-full bg-muted overflow-hidden">
                <div className="h-full rounded-full bg-primary/70" style={{ width: `${teto > 0 ? (p.meuPreco! / teto) * 100 : 0}%` }} />
                {p.precoPraca != null && teto > 0 && (
                  <span
                    className="absolute top-0 h-full w-0.5 bg-foreground/70"
                    style={{ left: `${(p.precoPraca / teto) * 100}%` }}
                    title={`Praça: ${fmtMoedaBR(p.precoPraca)}`}
                  />
                )}
              </div>
              <p className="text-[11px] text-muted-foreground">
                {p.vendas} {p.vendas === 1 ? "venda" : "vendas"} · {fmtKgBR(p.kg)}
                {p.precoPraca != null && <> · praça {fmtMoedaBR(p.precoPraca)}</>}
              </p>
            </li>
          ))}
        </ul>
      )}

      {/* Só quando é positivo: "se tivesse vendido na praça" é argumento para a
          próxima carga, não um saldo — e um número negativo aqui leria como
          dívida. `null` (placa de VENDA) some pelo mesmo `> 0`: a frase não se
          aplica quando as bases não batem, e o servidor é quem decide isso. */}
      {dados.seNaPraca != null && dados.seNaPraca > 0 && (
        <footer className="border-t bg-muted/40 px-3 py-2 text-xs text-muted-foreground flex items-center justify-between">
          <span>Se tivesse vendido na praça</span>
          <span className="font-mono tabular-nums font-semibold text-foreground" data-testid="texto-se-na-praca">
            + {fmtMoedaBR(dados.seNaPraca)}
          </span>
        </footer>
      )}
      {dados.itensNaoMapeados > 0 && (
        <p className="border-t px-3 py-1.5 text-[11px] text-atencao" data-testid="texto-itens-nao-mapeados">
          {dados.itensNaoMapeados} {dados.itensNaoMapeados === 1 ? "item sem produto mapeado" : "itens sem produto mapeado"} — fora desta conta.
        </p>
      )}
    </section>
  );
}
